import React from "react";
import { Link } from "react-router-dom";
import { AppWindow, ArrowUpRight, Factory, Server, ShoppingCart, Sparkles } from "lucide-react";
import Reveal, { RevealGroup, RevealItem } from "../ui/Reveal";
import DirArrow from "../ui/DirArrow";
import { solutionPath } from "../../lib/solutions";
import useLang from "../../hooks/useLang";

const VISIBLE_OFFERINGS = 4;

const CHIP_STYLES = {
  web: "bg-white/10 border-white/10 text-white/90",
  ai: "bg-white/15 border-white/20 text-white",
  ecommerce: "bg-white border-zinc-200 text-ink",
  erp: "bg-white border-zinc-200 text-ink",
  backend: "bg-white border-zinc-200 text-ink",
  more: "bg-white/10 border-white/10 text-white/90",
};

const TILE_STYLES = {
  web: "bg-ink text-white dark:ring-1 dark:ring-inset dark:ring-white/10",
  ai: "bg-gradient-to-br from-rose-start to-rose text-white",
  ecommerce: "bg-zinc-100 text-ink border border-zinc-200",
  erp: "bg-zinc-100 text-ink border border-zinc-200",
  backend: "bg-zinc-100 text-ink border border-zinc-200",
  more: "bg-zinc-800 text-white",
};

// Equal heights, staggered widths on a 12-column grid: 5/4/3 then 3/4/5.
const TILE_SPANS = {
  web: "lg:col-span-5",
  ai: "lg:col-span-4",
  ecommerce: "lg:col-span-3",
  erp: "lg:col-span-3",
  backend: "lg:col-span-4",
  more: "lg:col-span-5",
};

const TILE_ICONS = {
  web: AppWindow,
  ai: Sparkles,
  ecommerce: ShoppingCart,
  erp: Factory,
  backend: Server,
};

const WIDE_TILES = ["web", "more"];

// What We Do: one tile per solution category; hovering reveals its solutions.
const WhatWeDoSection = () => {
  const { t } = useLang();
  const categories = ["web", "ai", "ecommerce", "erp", "backend", "more"];

  return (
    <section id="what-we-do" className="py-16 sm:py-20 bg-white scroll-mt-[72px]">
      <div className="container-site">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <h2 className="h-section font-extrabold text-ink">{t("whatWeDo.title")}</h2>
          </div>
          <p className="text-zinc-500 max-w-sm leading-relaxed">{t("whatWeDo.subtitle")}</p>
        </Reveal>
        <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[300px] gap-3 sm:gap-4">
          {categories.map((key, i) => {
            const dark = !TILE_STYLES[key].includes("zinc-100");
            const wide = WIDE_TILES.includes(key);
            const Icon = TILE_ICONS[key];
            const offerings = t(`whatWeDo.categories.${key}.offerings`, { returnObjects: true });
            return (
              <RevealItem key={key} className={`h-full ${TILE_SPANS[key]}`}>
                <Link
                  to={solutionPath(key)}
                  className={`group relative overflow-hidden flex flex-col h-full min-h-[260px] rounded-2xl p-7 transition-[transform,box-shadow] duration-500 ease-out-expo hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(10,10,10,0.35)] focus-visible:-translate-y-1 ${TILE_STYLES[key]}`}
                >
                  {Icon && (
                    <Icon
                      aria-hidden="true"
                      strokeWidth={1.5}
                      size={wide ? 44 : 28}
                      className={`absolute top-6 end-6 transition-transform duration-500 ease-out-expo group-hover:scale-110 group-hover:-rotate-6 ${
                        key === "web" ? "text-white/30" : dark ? "text-white/80" : "text-rose"
                      }`}
                    />
                  )}
                  <span className={`font-mono text-[11px] tracking-[0.16em] ${dark ? "text-white/50" : "text-zinc-400"}`}>
                    0{i + 1}
                  </span>
                  <div className={`mt-auto pt-8 ${key === "more" ? "pe-16" : ""}`}>
                    <h3 className={`font-extrabold tracking-[-0.02em] ${wide ? "text-2xl" : "text-xl"}`}>
                      {t(`whatWeDo.categories.${key}.title`)}
                    </h3>
                    {/* Description by default; the offerings slide in over it on hover or keyboard focus. */}
                    <div className="grid mt-2">
                      <p
                        className={`[grid-area:1/1] text-sm leading-relaxed transition-all duration-500 ease-out-expo group-hover:opacity-0 group-hover:-translate-y-2 group-focus-visible:opacity-0 group-focus-visible:-translate-y-2 ${
                          wide ? "max-w-xs" : ""
                        } ${dark ? "text-white/65" : "text-zinc-500"}`}
                      >
                        {t(`whatWeDo.categories.${key}.desc`)}
                      </p>
                      <ul className="[grid-area:1/1] flex flex-wrap content-start gap-1.5" aria-hidden="true">
                        {offerings.slice(0, VISIBLE_OFFERINGS).map((o, k) => (
                          <li
                            key={o.title}
                            style={{ transitionDelay: `${60 + k * 45}ms` }}
                            className={`text-xs font-semibold rounded-full border px-2.5 py-1 opacity-0 translate-y-3 transition-all duration-500 ease-out-expo group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0 ${CHIP_STYLES[key]}`}
                          >
                            {o.title}
                          </li>
                        ))}
                        {offerings.length > VISIBLE_OFFERINGS && (
                          <li
                            style={{ transitionDelay: `${60 + VISIBLE_OFFERINGS * 45}ms` }}
                            className={`text-xs font-bold px-1.5 py-1 opacity-0 translate-y-3 transition-all duration-500 ease-out-expo group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0 ${
                              dark ? "text-white/70" : "text-rose-dark"
                            }`}
                          >
                            {t("whatWeDo.more", { count: offerings.length - VISIBLE_OFFERINGS })}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  {key === "more" ? (
                    <span className="absolute bottom-7 end-7 grid place-items-center w-12 h-12 rounded-full bg-gradient-to-br from-rose-start to-rose text-white transition-transform duration-500 ease-out-expo group-hover:scale-110 group-hover:rotate-45 rtl:-scale-x-100">
                      <ArrowUpRight size={22} aria-hidden="true" />
                    </span>
                  ) : (
                    <span className={`link-arrow pt-5 ${key === "ai" ? "text-white" : dark ? "text-white" : "text-rose-dark"}`}>
                      {t("whatWeDo.explore")} <DirArrow nudge size={14} />
                    </span>
                  )}
                </Link>
              </RevealItem>
            );
          })}
        </RevealGroup>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
